import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import { CheckCircle } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { StructureModal } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Flex } from "@similarweb/ui-components/foundations";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { useApiQuery } from "app/modules/api-client";
import { createKeywordGroupEntity } from "app/modules/demand-analysis/keyword-lists";
import { useCreateOrUpdateKeywordGroup } from "app/services/keyword-groups/hooks";
import { allTrackers } from "app/services/track/track";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useKeywordListsNodeAPIQueryParams } from "../../hooks/useKeywordListsNodeAPIQueryParams";
import type { DemandAnalysisSearchTrendsQueryParams } from "../../types";

type SearchTrendConversionModalProps = {
    seed: string;
    type: "aiTopic" | "searchTerm";
    isOpen: boolean;
    onClose: () => void;
};

export const SearchTrendsConversionModal = ({ seed, isOpen, onClose }: SearchTrendConversionModalProps) => {
    const t = useTranslation();
    const dispatch = useDispatch();

    const { params } = useCurrentStateAndParams<DemandAnalysisSearchTrendsQueryParams>();

    const [searchTermListName, setSearchTermListName] = useState(seed);
    const [hasNameConflict, setHasNameConflict] = useState(false);

    const queryParams = useKeywordListsNodeAPIQueryParams();
    const {
        data: searchTrendsData,
        isLoading: isSearchTrendsLoading,
        error: searchTrendsError,
    } = useApiQuery(
        {
            tag: "DemandSearchTrends",
            methodName: "marketResearchDemandAnalysisSearchTrendsList",
        },
        queryParams,
    );

    const hasSearchTrendsError = !!searchTrendsError;
    const hasSearchTrendsData = !!searchTrendsData;

    // Function to extract keywords from search trends data
    const getKeywordsFromTrends = (): string[] => {
        const keywords: string[] = [];
        if (searchTrendsData?.children) {
            searchTrendsData.children.forEach((child) => {
                keywords.push(child.name);
            });
        }
        return keywords;
    };

    const { mutateAsync, reset, isPending } = useCreateOrUpdateKeywordGroup();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSearchTermListName(seed);
        setHasNameConflict(false);

        let isStale = false;

        void (async () => {
            const foundGroup = await KeywordsGroupUtilities.getUserGroupByName(seed);
            if (!isStale && seed === foundGroup?.Name) {
                setHasNameConflict(true);
            }
        })();

        return () => {
            isStale = true;
        };
    }, [seed, isOpen]);

    const handleChange = async (value: string) => {
        setSearchTermListName(value);

        const keywordGroupExists = await KeywordsGroupUtilities.getUserGroupByName(value);

        if (keywordGroupExists && Object.keys(keywordGroupExists).length > 0) {
            setHasNameConflict(true);
            reset();
            return;
        }

        setHasNameConflict(false);
    };

    const showToast = (status: "success" | "error") => {
        switch (status) {
            case "success": {
                const successContent = (
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <CheckCircle
                            size="sm"
                            color={colorsPalettes.green["300"]}
                        />
                        {t("create.list.wizard.toast", { listName: searchTermListName })}
                    </Flex>
                );
                dispatch(showSuccessToast(successContent));
                return;
            }
            case "error": {
                const errorContent = (
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <CheckCircle
                            size="sm"
                            color={colorsPalettes.red["300"]}
                        />
                        {t("create.list.wizard.toast.error", { listName: searchTermListName })}
                    </Flex>
                );
                dispatch(showErrorToast(errorContent));
                return;
            }
        }
    };

    const handleSearchTermSave = async () => {
        try {
            const keywords = getKeywordsFromTrends();
            const newKeywordGroup = await mutateAsync({
                Name: searchTermListName,
                Keywords: keywords,
            });

            if (!newKeywordGroup.Id || !newKeywordGroup.Name) {
                throw new Error("Failed to save search term as new keyword list");
            }

            // Track the successful save
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `save as list/save/${searchTermListName}/${keywords.length}`,
                { KW_list_type: "search term", KW_list_name: seed },
            );

            const keywordListEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId: newKeywordGroup.Id },
                newKeywordGroup.Name,
            );

            const newParams: DemandAnalysisSearchTrendsQueryParams = {
                ...params,
                id: [keywordListEntity.keywordListId],
            };

            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, newParams, {
                inherit: false,
                reload: true,
            });

            showToast("success");
        } catch (error: any) {
            if (error.status === 409) {
                setHasNameConflict(true);
                return;
            }

            showToast("error");
            Sentry.error("Failed to save search term as new keyword list: ", error);
        }
    };

    const handleCancelButtonClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "save as list/cancel", {
            KW_list_type: "search term",
            KW_list_name: seed,
        });
        onClose();
    };

    return (
        <StructureModal
            isOpen={isOpen}
            showHeaderDivider={false}
            customStyles={{ content: { width: "400px", height: "220px" } }}
            title={t("keyword.analysis.subnav.search.term.save.button.text")}
            mainButton={{
                type: "primary",
                label: t("keyword.analysis.subnav.search.term.save.button.text"),
                tooltipEnabled: false,
                isLoading: isPending || isSearchTrendsLoading,
                isDisabled: !searchTermListName || hasNameConflict || !hasSearchTrendsData || hasSearchTrendsError,
                onClick: handleSearchTermSave,
            }}
            secondaryButton={{
                type: "flat",
                label: t("common.cancel"),
                tooltipEnabled: false,
                onClick: handleCancelButtonClick,
            }}
        >
            <TextField
                value={searchTermListName}
                label={t("keyword.analysis.subnav.search.term.save.modal.label")}
                onChange={handleChange}
                isInvalid={hasNameConflict}
                errorMessage={t("keyword.analysis.subnav.search.term.save.modal.error", {
                    listName: searchTermListName,
                })}
            />
        </StructureModal>
    );
};
